import type { CVAccentColor, CVConfigData, CVLayoutStyle } from '@tabbio/types';
// Resolves at runtime via the extensionless bridge file `./cv-share-card`
// (see packages/utils/src/cv-share-card): the repo's pattern for utils modules
// imported extensionlessly under Node's ESM loader.
import {
  coerceShareCardForEntitlement,
  normalizeShareCardConfig,
} from './cv-share-card';

export const VALID_CV_LAYOUT_STYLES: CVLayoutStyle[] = [
  'MODERN',
  'CLASSIC',
  'EXECUTIVE',
  'CREATIVE',
];

export const VALID_CV_ACCENT_COLORS: CVAccentColor[] = [
  'PURPLE',
  'BLACK',
  'BLUE',
  'EMERALD',
  'RED',
  'ORANGE',
];

export const DEFAULT_CV_CONFIG: CVConfigData = {
  style: 'MODERN',
  color: 'BLACK',
  showPhoto: true,
};

type CvActionConfig = {
  showPhoto: boolean;
  fitToOnePage?: boolean;
  hideTabbioLink?: boolean;
};

/**
 * Change only the CV-layout photo flag. Keeping this as a named transform
 * prevents photo controls from rebuilding the full config (and accidentally
 * changing the independent one-page export preference).
 */
export function withCvPhotoVisibility<T extends CvActionConfig>(
  config: T,
  showPhoto: boolean,
): Omit<T, 'showPhoto'> & { showPhoto: boolean } {
  return { ...config, showPhoto };
}

/**
 * Change only the one-page export preference. `false` removes the optional
 * key so the result matches the canonical normalized storage shape.
 */
export function withCvFitToOnePage<T extends CvActionConfig>(
  config: T,
  enabled: boolean,
): Omit<T, 'fitToOnePage'> & { fitToOnePage?: true } {
  const next = { ...config } as Omit<T, 'fitToOnePage'> & {
    fitToOnePage?: true;
  };
  if (enabled) {
    next.fitToOnePage = true;
  } else {
    delete next.fitToOnePage;
  }
  return next;
}

/**
 * Change only the "hide the Tabbio link" preference (Pro). Like
 * `withCvFitToOnePage`, `false` removes the optional key so the result matches
 * the canonical normalized storage shape instead of writing `false` out.
 */
export function withCvHideTabbioLink<T extends CvActionConfig>(
  config: T,
  hidden: boolean,
): Omit<T, 'hideTabbioLink'> & { hideTabbioLink?: true } {
  const next = { ...config } as Omit<T, 'hideTabbioLink'> & {
    hideTabbioLink?: true;
  };
  if (hidden) {
    next.hideTabbioLink = true;
  } else {
    delete next.hideTabbioLink;
  }
  return next;
}

export function normalizeCvConfig(value: unknown): CVConfigData {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { ...DEFAULT_CV_CONFIG };
  }

  const input = value as Record<string, unknown>;
  // Preserve the optional share/OG card config. normalizeCvConfig runs on every
  // read path (master, tailored resolve, public page) so omitting this would
  // silently drop the card on round-trip.
  const card = normalizeShareCardConfig(input.card);
  return {
    style: VALID_CV_LAYOUT_STYLES.includes(input.style as CVLayoutStyle)
      ? (input.style as CVLayoutStyle)
      : DEFAULT_CV_CONFIG.style,
    color: VALID_CV_ACCENT_COLORS.includes(input.color as CVAccentColor)
      ? (input.color as CVAccentColor)
      : DEFAULT_CV_CONFIG.color,
    showPhoto: typeof input.showPhoto === 'boolean'
      ? input.showPhoto
      : DEFAULT_CV_CONFIG.showPhoto,
    // Off by default; only an explicit true survives normalization so stored
    // configs stay minimal and legacy shapes round-trip byte-identical.
    ...(input.fitToOnePage === true ? { fitToOnePage: true } : {}),
    // Same rule as fitToOnePage: only an explicit true survives, so a config
    // that never touched the Tabbio link stays byte-identical on round-trip.
    ...(input.hideTabbioLink === true ? { hideTabbioLink: true } : {}),
    ...(card ? { card } : {}),
  };
}

/**
 * Server- and client-side paywall coercion for the whole CV config.
 *
 * Both branding-gated fields live here so a caller cannot fix one and forget
 * the other: the share/OG card's Pro fields (`coerceShareCardForEntitlement`)
 * and `hideTabbioLink`. Callers pass the result of a single
 * `individual.custom_branding` evaluation, and must fail closed (pass `false`)
 * when that lookup is unavailable.
 *
 * Returns the input untouched when nothing changes, so read paths can keep
 * their identity checks.
 */
export function coerceCvConfigForEntitlement<
  T extends {
    hideTabbioLink?: boolean;
    card?: Parameters<typeof coerceShareCardForEntitlement>[0];
  },
>(config: T, allowed: boolean): T {
  if (allowed) return config;
  const card = coerceShareCardForEntitlement(config.card, allowed);
  const cardChanged = card !== config.card;
  const linkChanged = config.hideTabbioLink === true;
  if (!cardChanged && !linkChanged) return config;
  const next = { ...config, ...(cardChanged ? { card } : {}) };
  delete next.hideTabbioLink;
  return next;
}
